import type { ReactNode } from 'react';
import { Panel } from '../Panel.tsx';
import { Blank } from './Blank.tsx';
import { blankLine, faultTone } from './fault.ts';
import { FaultLine } from './Faults.tsx';
import { ReadingNote } from './Reading.tsx';
import { NotCurrent, ReadTag } from './ReadTag.tsx';
import { HeaderBar, StoppedContext } from './stopped.ts';
import { useBannerCarries, type Read } from './read.ts';
import styles from './states.module.css';

/* ==========================================================================
   The states, wired to one read, in one component.

   Every workspace in this console had rolled its own version of this by the
   time it was written, and they disagreed — on whether a failed refresh
   blanks the panel, on whether "Loading…" is a sentence, on whether a filter
   that excludes everything says "No robots". This is the one that survives.

   ---------------------------------------------------------------------------
   ONE FAILURE LANGUAGE
   ---------------------------------------------------------------------------
   There used to be two, and they contradicted each other:

     data on screen, read failed   keep every figure, dim it, put a fault bar
                                   with a Retry under the panel header
     nothing on screen, read failed   throw the layout away and draw a page of
                                      diagnostics where the panel was

   They are the same event — the panel has stopped being supplied — and the
   only difference is whether what sits under the fault bar is real figures or
   the instrument the loading state was already drawing. That is worth one
   extra sentence, not a different design.

   So both render the SAME two things in the SAME order:

     the fault bar, flush to the panel's edges, one sentence and one verb
     the panel's content, kept, marked as not current

   and the only thing that changes is what "content" is:

     first     a read is in flight and nothing has landed  → the instrument
     live      figures on screen, reads landing            → the data
     stalled   figures on screen, the last read failed     → the data, kept at
                                                             full strength
     blank     nothing on screen, the last read failed     → the instrument,
                                                             aria-busy off
     empty     the read landed and carried nothing         → the instrument,
                                                             with no blocks and
                                                             one line in it

   ---------------------------------------------------------------------------
   AN EMPTY PANEL KEEPS ITS INSTRUMENT
   ---------------------------------------------------------------------------
   `skeleton` is not a loading placeholder, it is THE INSTRUMENT: the column
   headings, the metric labels, the lane rules, the threshold lines, the axis.
   Every one of those facts is true before the first read, after a failed read,
   and when a read lands with nothing in it — so the same drawing serves all
   three, and `empty` renders it too, in the mode that takes the blocks off,
   takes the anonymous ROWS off with them, and puts one line of --text-mid
   where the first row would be.

   The alternative — render `children`, which draws nothing, plus a sentence —
   is how `/telemetry` with no readings used to delete an eight-channel plot
   and leave 836px of ground, and how a register with no rows used to delete
   its own column headings. A panel is the same size, the same shape and the
   same instrument in all five of its states.

   Nothing on this surface is ever replaced by an explanation of itself.
   ========================================================================== */

/* THE FAULT LINE SAYS WHY. THE ROW SAYS WHAT IS THERE. BOTH ARE OWED.

   This used to pass `note: null` — deliberate silence — on the theory that the
   `FaultLine` three lines below already stated the event. It does state the
   event, and the result was /fleet/list rendering a red bar, eleven column
   headings and then six hundred pixels of nothing: an operator scrolling into
   that reads a table that failed to paint, not a table with no rows in it.

   The two lines answer different questions and sit in different places. The
   fault line is flush to the panel's edge and carries the cause — the
   endpoint, the status, the run count. The row is inside the instrument, at
   the first row's position, in --text-mid, and says only that there is nothing
   there. That is the same two-part shape the workspaces that roll their own
   failure chrome already take, so the geometry reads identically across the
   surface whoever drew it. `useStanding` supplies the words. */
const STOPPED = { mode: 'stopped' } as const;

export interface ReadBodyProps {
  read: Read;
  /**
   * THE INSTRUMENT — the shape of the content with its real chrome. Drawn on
   * the first read, with its blocks and the 1px sweep; drawn again with
   * neither, and a fault row above it, when that read failed; and drawn a
   * third time with the `empty` line inside it when a read lands carrying
   * nothing. The layout is true in all three, so it never leaves the screen.
   */
  skeleton: ReactNode;
  /**
   * The read landed and carried nothing: ONE line, from `blankLine`, rendered
   * at the first row's position INSIDE the instrument above. Not a replacement
   * for the panel's content — a line in it.
   */
  empty?: ReactNode;
  /**
   * The way OUT of a 404 — "Back to the fleet". Not a Retry: a retry cannot
   * make a deregistered robot exist, and offering one is the console telling
   * the operator to keep pressing.
   */
  leave?: { label: string; onSelect: () => void } | undefined;
  /**
   * The content is a viewport — a camera frame, a point cloud — whose pixels
   * ARE the reading. The one thing this module dims when it stops: 40%, with
   * the age kept, per THE BAR §5. Figures are never dimmed.
   */
  viewport?: boolean | undefined;
  /**
   * The panel this body sits in already prints a `ReadTag` in its header, so
   * the fault line must not repeat the age. Set by `ReadPanel`; a caller that
   * pairs a bare `Panel` with a `ReadBody` leaves it off and the fault line
   * becomes the panel's only freshness chrome — which is the whole reason the
   * age is on it. Not a style flag: it decides which of two places states one
   * fact, and stating it in both is how a header stops being read.
   */
  tagged?: boolean | undefined;
  /**
   * The panel inset, applied to the CONTENT only. The fault line keeps the
   * panel's own text edge and never the content's, so it lands on the same
   * left edge as the column headings below it in every panel on the surface.
   */
  pad?: 'default' | 'none' | undefined;
  children: ReactNode;
}

export function ReadBody({
  read,
  skeleton,
  empty,
  leave,
  viewport = false,
  tagged = false,
  pad = 'none',
  children,
}: ReadBodyProps) {
  /* Cheap and tick-free: the surface publishes its verdict as a context value
     that changes only when the diagnosis does, so eight panels learn it
     without eight 1 Hz subscriptions. See `useBannerCarries`. */
  const bannerCarries = useBannerCarries();

  const inset = (node: ReactNode) =>
    pad === 'default' ? <div className={styles.pad}>{node}</div> : <>{node}</>;

  /* No bar here, deliberately. THE BAR §7 allows ONE indeterminate bar per
     loading panel, and every surface that mounts a ReadBody mounts it inside a
     `Panel` that is already drawing one under its own header — a second one
     8px lower reads as a 2px bar on one route and as two bars on another.
     Where a panel has no bar during its first read, that is the panel's own
     `loading` prop to pass, and it owns its header.

     WHAT IS NEW HERE IS THE AGE. The instrument's `Reading…` line is drawn
     from a context that carries no query identity, so on its own it can only
     report the surface's oldest unanswered read. This body HOLDS the read, so
     it hands over the panel's own wait and the line becomes exact: two panels
     on one route reading two endpoints at two different speeds each print
     their own duration. */
  if (read.phase === 'first') {
    return inset(
      <StoppedContext.Provider
        value={{
          mode: 'reading',
          note: (
            <Blank>
              <ReadingNote elapsedMs={read.waitingMs} />
            </Blank>
          ),
        }}
      >
        {skeleton}
      </StoppedContext.Provider>,
    );
  }

  const fault = read.fault;

  /* A 404 IS NOT AN OUTAGE, AND IT IS NOT A FAULT BAR.

     The API answered completely and correctly; the object is not there. That
     is the same class of fact as "this robot has issued no commands" — an
     empty state — and it gets an empty state's treatment: one --text-mid line
     at the first row's position, under the caller's own headings, with the
     endpoint and the status in mono beside it and the way out as the single
     verb. It used to be one --error-coloured sentence centred in a viewport
     of void, which told an operator that the console had broken when what had
     actually happened was that they had typed a robot id that no longer
     exists. Same for a 403: the account cannot read this, and no amount of
     retrying changes an entitlement. */
  if (fault !== null && (fault.class === 'missing' || fault.class === 'forbidden')) {
    const where: string[] = [];
    if (read.path !== null) where.push(read.path);
    if (fault.status !== null) where.push(String(fault.status));
    const line = (
      <Blank
        kind={fault.class === 'missing' ? 'missing' : 'withheld'}
        detail={where.join('  ·  ')}
        {...(leave !== undefined ? { action: leave } : {})}
      >
        {blankLine(fault.class === 'missing' ? 'missing' : 'withheld', {
          subject: read.subject,
        })}
      </Blank>
    );
    /* A robot deregistered while the operator was looking at it: the object is
       gone, and the figures they were reading are still the last thing it
       reported. Both are true, so both stay — the line says what happened and
       the data below it goes not-current, exactly as a stalled panel does.
       Throwing the last known state of a machine away at the moment it leaves
       the fleet is the worst time to do it. */
    if (read.hasData) {
      return (
        <div className={styles.stack}>
          {inset(line)}
          <NotCurrent stopped shape="stale" viewport={viewport}>
            {inset(children)}
          </NotCurrent>
        </div>
      );
    }
    /* Nothing was ever read, and now the API has told us there is nothing to
       read. That is an ABSENCE, so it gets an absence's treatment: the
       instrument, with the line at the first row's position inside it. A 404
       used to be one --error-coloured sentence centred in a viewport of void,
       which told an operator the console had broken when what had actually
       happened was that they had typed an id that no longer exists. */
    return inset(
      <StoppedContext.Provider value={{ mode: 'empty', note: line }}>
        {skeleton}
      </StoppedContext.Provider>,
    );
  }

  /* The empty line stands in for the first ROW, not for the panel: the
     instrument above — a table's column headings, a plot's lane labels and
     threshold rules, a matrix's metric lanes — is known before the data and
     stays drawn, and the line sits inside it exactly where the first row would
     be. Replacing the body instead is how a panel loses its headings the
     moment it has nothing in it. */
  const content =
    read.phase === 'blank' ? (
      <StoppedContext.Provider value={STOPPED}>{skeleton}</StoppedContext.Provider>
    ) : read.empty && empty !== undefined ? (
      <StoppedContext.Provider value={{ mode: 'empty', note: empty }}>
        {skeleton}
      </StoppedContext.Provider>
    ) : (
      children
    );

  const body = inset(content);

  if (read.phase === 'live') return <>{body}</>;

  /* THE TWO FAILURE STATES ARE ONE PICTURE.

     `stalled` has real figures under the line and `blank` has the instrument
     under it, and that is the ONLY difference between them: same line, same
     position, same words, same full-strength content. The console used to
     answer them with two incompatible designs — keep-and-dim on one path,
     blank-and-diagnose on the other — and an operator had to learn both.

     ------------------------------------------------------------------------
     ONE FACT, ONE PLACE — WHY THE LINE IS NOT ALWAYS DRAWN
     ------------------------------------------------------------------------
     The fault line exists to say the ONE thing that is this panel's alone:
     which source it was waiting on. On a `partial` outage that is the only
     place the fact appears, and it is indispensable —
     `/api/robots/rover-01/telemetry` dying under a healthy console is exactly
     the fault an operator has to be able to attribute to a panel.

     When the whole surface has stopped it is not this panel's alone. Every
     panel is waiting on the same dead link, the banner two rows up already
     names the endpoints, the silence and the next attempt, and the supply
     ledger at the foot lists every one of them with its cadence and its last
     good stamp. Measured on one 1600×1000 `/fleet` frame at 30s offline:
     `no reply` ×17, `Unreachable` ×7, `Nothing has been read.` ×8,
     `/api/robots` ×9 — 41 restatements of one event, and eight panels paying
     ~44px each for their share of it.

     So on a stopped surface a BLANK panel keeps THE BAR §5's third encoding
     and drops the prose: a 4px `--error` leading edge on the band it actually
     says something in, which is the same mark the exception rows wear and is
     found by the same scan, plus the `no reply` tag already in its header.
     Colour is not carrying it alone — the header tag is a word, and the body
     still says, in one line at the first row's position, that there is nothing
     there.

     A STALLED PANEL KEEPS ITS LINE, and that is not an inconsistency. It has
     figures on screen and the one fact nothing else on the surface can state
     is HOW OLD THIS PANEL'S figures are: the banner's silence is the console's
     oldest read, the header tag is 24px away in a different type, and the
     number an operator adds to `18h ago` in row four is this panel's. The
     restatement being removed is the one with nothing under it. */
  const edge = bannerCarries && read.phase === 'blank' ? faultTone(fault?.class ?? 'unknown') : null;

  return (
    <div className={styles.stack} {...(edge !== null ? { 'data-edge': edge } : {})}>
      {edge === null && <FaultLine read={read} age={!tagged} />}
      <NotCurrent stopped viewport={viewport} shape={read.phase === 'blank' ? 'absent' : 'stale'}>
        {body}
      </NotCurrent>
    </div>
  );
}

/**
 * A Panel already wired to a read: the freshness tag in `meta`, the re-read
 * verb in `tools`, the 1px indeterminate bar under the header on a refresh,
 * and the body above. Adopting it should be a three-line diff in any
 * workspace.
 */
export function ReadPanel({
  title,
  count,
  meta,
  tools,
  scroll,
  className,
  ...body
}: ReadBodyProps & {
  title?: ReactNode;
  count?: number | string | undefined;
  /** Anything else for the header's right side; the read tag is added last. */
  meta?: ReactNode;
  tools?: ReactNode;
  scroll?: boolean | undefined;
  className?: string | undefined;
}) {
  return (
    <Panel
      title={title}
      count={count}
      meta={
        <>
          {meta}
          <ReadTag read={body.read} />
        </>
      }
      tools={tools}
      /* THE ONE MOVING THING, AND THE ONLY THING THAT SEPARATES "LOADING"
         FROM "STOPPED". Nothing dims when a source stops (states.module.css),
         so this bar is not decoration: it runs whenever a request is genuinely
         in flight — first read, refresh, or retry — and stops dead the moment
         it is not. Asking an operator to judge "is this loading or is it
         broken" off 20% of alpha was the defect; a bar that is either moving
         or absent is the answer. The only continuous motion this module
         permits (THE BAR §7). */
      loading={body.read.fetching}
      pad="none"
      scroll={scroll}
      className={className}
    >
      {/* The header above is already running the console's one indeterminate
          bar, so the instrument inside must not run a second one 1px lower.
          See `HeaderBar` in stopped.ts: the shapes draw it by default, because
          most callers pair a bare `Panel` with a skeleton and pass no
          `loading` at all, and this is the one place that can say otherwise. */}
      <HeaderBar.Provider value>
        <ReadBody {...body} tagged />
      </HeaderBar.Provider>
    </Panel>
  );
}
